import type { FC } from "react";
import { useApolloClient } from "@apollo/client";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import { useTranslation } from "@/shared/hooks";
import {
  AssistantMessageRatingType,
  type RateAssistantMessageMutationType,
  type RateAssistantMessageMutationVariablesType,
} from "@/types/graphql/site.generated";
import { RateAssistantMessageMutation } from "../api/graphql";
import type { AssistantBlockedReason } from "../api/streamAssistantMessage";
import { renderAssistantMarkdown } from "../lib/renderAssistantMarkdown";
import {
  setMessageRating,
  type AssistantUiMessage,
} from "../model/assistantStore";
import { AssistantRequestCard } from "./AssistantRequestCard";
import { AssistantAvatar } from "./AssistantAvatar";

const BLOCKED_KEYS: Record<AssistantBlockedReason, string> = {
  DISABLED: "assistant.blockedDisabled",
  BUDGET: "assistant.blockedBudget",
  DAILY_LIMIT: "assistant.blockedDailyLimit",
  OFF_TOPIC_BLOCK: "assistant.blockedOffTopic",
};

const RATINGS: Record<"UP" | "DOWN", AssistantMessageRatingType> = {
  UP: AssistantMessageRatingType.UpType,
  DOWN: AssistantMessageRatingType.DownType,
};

interface AssistantMessageItemProps {
  message: AssistantUiMessage;
  assistantName: string;
  avatarUrl: string | null;
  arenaTitle: string;
  supportEmail: string | null;
  viewerEmail: string | null;
  onInternalLinkClick?: () => void;
  /**
   * Спросить «Помогло?» вместо значков оценки, пока ответ не оценён:
   * первый ответ в диалоге, начатом с кнопки экрана ошибки (спека
   * 2026-10-29 V8). Ответ пишется в тот же rating, что и лайк/дизлайк.
   */
  askHelpful?: boolean;
}

const TypingDots: FC = () => (
  <Box
    component="span"
    aria-hidden
    sx={{
      display: "inline-flex",
      gap: 0.5,
      py: 0.75,
      "& > span": {
        width: 6,
        height: 6,
        borderRadius: "50%",
        bgcolor: "text.secondary",
        animation: "assistantTyping 1.2s infinite ease-in-out",
      },
      "& > span:nth-of-type(2)": { animationDelay: "0.2s" },
      "& > span:nth-of-type(3)": { animationDelay: "0.4s" },
      "@keyframes assistantTyping": {
        "0%, 80%, 100%": { opacity: 0.3 },
        "40%": { opacity: 1 },
      },
    }}
  >
    <span />
    <span />
    <span />
  </Box>
);

export const AssistantMessageItem: FC<AssistantMessageItemProps> = ({
  message,
  assistantName,
  avatarUrl,
  arenaTitle,
  supportEmail,
  viewerEmail,
  onInternalLinkClick,
  askHelpful = false,
}) => {
  const { t } = useTranslation();
  const client = useApolloClient();

  if (message.role === "user") {
    return (
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Box
          sx={{
            maxWidth: "85%",
            px: 1.5,
            py: 1,
            borderRadius: 2,
            borderBottomRightRadius: 4,
            bgcolor: "primary.main",
            color: "primary.contrastText",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            typography: "body2",
          }}
        >
          {message.text}
        </Box>
      </Box>
    );
  }

  // Ход завершён, но TEXT-ответ пришёл пустым (без request): по сути та же
  // ошибка, что и kind ERROR, просто состояние не успело смениться на
  // сервере или в сохранённой ленте (hydrate берёт kind как есть).
  const emptyTextDone =
    message.kind === "TEXT" &&
    !message.pending &&
    !message.text &&
    !message.request;

  let body;
  if (message.kind === "OFF_TOPIC") {
    body = <p>{t("assistant.offTopic", { arena: arenaTitle })}</p>;
  } else if (message.kind === "BLOCKED") {
    body = (
      <p>
        {supportEmail
          ? t(BLOCKED_KEYS[message.blockedReason ?? "BUDGET"], {
              email: supportEmail,
            })
          : t("assistant.unavailableNoEmail")}
      </p>
    );
  } else if (message.kind === "ERROR" || emptyTextDone) {
    body = (
      <p>
        {supportEmail
          ? t("assistant.error", { email: supportEmail })
          : t("assistant.errorNoEmail")}
      </p>
    );
  } else if (message.pending && !message.text) {
    body = <TypingDots />;
  } else {
    body = renderAssistantMarkdown(message.text, { onInternalLinkClick });
  }

  // Оценивать можно только сохранённый ответ: у него уже серверный id.
  const canRate =
    message.kind === "TEXT" &&
    !message.pending &&
    !message.id.startsWith("tmp-") &&
    !emptyTextDone;

  const rate = async (value: "UP" | "DOWN") => {
    const previous = message.rating;
    const next = previous === value ? null : value;
    setMessageRating(message.id, next);
    try {
      await client.mutate<
        RateAssistantMessageMutationType,
        RateAssistantMessageMutationVariablesType
      >({
        mutation: RateAssistantMessageMutation,
        variables: { id: message.id, rating: next && RATINGS[next] },
      });
    } catch {
      setMessageRating(message.id, previous);
    }
  };

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
      <AssistantAvatar avatarUrl={avatarUrl} alt={assistantName} size={28} />
      <Box sx={{ minWidth: 0, maxWidth: "85%" }}>
        <Box
          sx={(theme) => ({
            px: 1.5,
            py: 1,
            borderRadius: 2,
            borderTopLeftRadius: 4,
            bgcolor: alpha(theme.palette.text.primary, 0.06),
            color: message.kind === "TEXT" ? "text.primary" : "text.secondary",
            typography: "body2",
            wordBreak: "break-word",
            "& p": { m: 0 },
            "& p + p, & p + ul, & p + ol, & ul + p, & ol + p": { mt: 1 },
            "& ul, & ol": { m: 0, pl: 2.5 },
            "& li + li": { mt: 0.25 },
            "& a": {
              color: "inherit",
              textDecoration: "underline",
              textUnderlineOffset: 2,
              "&:hover": { color: "primary.main" },
            },
          })}
        >
          {body}
        </Box>
        {message.request && (
          <AssistantRequestCard
            request={message.request}
            viewerEmail={viewerEmail}
          />
        )}
        {canRate && askHelpful && message.rating === null && (
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{ mt: 1, alignItems: "center", flexWrap: "wrap" }}
          >
            <Typography variant="body2" color="text.secondary">
              {t("assistant.helpfulQuestion")}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => void rate("UP")}
            >
              {t("assistant.helpfulYes")}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => void rate("DOWN")}
            >
              {t("assistant.helpfulNo")}
            </Button>
          </Stack>
        )}
        {canRate && !(askHelpful && message.rating === null) && (
          <Box sx={{ display: "flex", gap: 0.5, mt: 0.5 }}>
            <IconButton
              size="small"
              aria-label={t("assistant.rateUp")}
              aria-pressed={message.rating === "UP"}
              color={message.rating === "UP" ? "primary" : "default"}
              onClick={() => void rate("UP")}
            >
              {message.rating === "UP" ? (
                <ThumbUpAltIcon fontSize="inherit" />
              ) : (
                <ThumbUpAltOutlinedIcon fontSize="inherit" />
              )}
            </IconButton>
            <IconButton
              size="small"
              aria-label={t("assistant.rateDown")}
              aria-pressed={message.rating === "DOWN"}
              color={message.rating === "DOWN" ? "primary" : "default"}
              onClick={() => void rate("DOWN")}
            >
              {message.rating === "DOWN" ? (
                <ThumbDownAltIcon fontSize="inherit" />
              ) : (
                <ThumbDownAltOutlinedIcon fontSize="inherit" />
              )}
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
  );
};
